'use client';

import { AlertTriangle } from 'lucide-react';
import posthog from 'posthog-js';
import { Component, ReactNode } from 'react';

import {
  claimChunkReloadOnce,
  extractChunkUrl,
  isChunkLoadFailure,
} from '@/components/chunk-load-reload-guard';
import { EmptyState } from '@/components/ui/empty-state';
import { mergeEnvelope } from '@/lib/analytics/context';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { reloadPage } from '@/lib/browser-navigation';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary to provide fallback UI for React errors.
 *
 * IMPORTANT: React error boundaries swallow errors before they reach window.onerror,
 * so PostHog's automatic capture_exceptions won't see them. We route through
 * logger.error, whose client sink captures a structured exception to PostHog.
 *
 * Chunk-load failures are the one exception to that: they're not app defects,
 * they're the "tab is older than the live deployment" race that
 * ChunkLoadReloadGuard already recovers from for window-level failures. A
 * failure thrown during React's render phase (e.g. an RSC navigation chunk
 * fetch — see PostHog issue 01a020f1-9a61-7920-a291-421d67a2a371) never
 * reaches ChunkLoadReloadGuard's window listeners, so
 * getDerivedStateFromError attempts the same one-shot recovery here instead
 * of waiting for componentDidCatch.
 *
 * This does NOT make the recovery invisible. Returning `{ hasError: false }`
 * makes React re-render `children` before reloadPage()'s navigation has
 * actually taken effect; for a genuine deployment-mismatch chunk failure the
 * underlying import is a cached rejection, so it throws again immediately.
 * React then re-invokes this method a second time — confirmed via direct
 * instrumentation against both react-dom's development AND production
 * bundles, so this isn't a test-only artifact — and by then the one-shot
 * slot is already spent, so THIS pass commits the fallback UI. The practical
 * result for a real user: a brief "Something went wrong" render, immediately
 * followed by the reload already in flight from the first pass — a large
 * improvement over today's permanent dead-end, but not the flash-free
 * recovery this may look like it's reaching for. See the "recovers from a
 * chunk-load failure" test's comment for the confirmed mechanics, and
 * docs/superpowers/specs/2026-08-20-chunk-load-recovery-error-boundary-design.md
 * for the original design rationale.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadFailure(error.message, '') && navigator.onLine && claimChunkReloadOnce()) {
      try {
        posthog.capture(
          AnalyticsEvents.CHUNK_LOAD_RELOAD,
          mergeEnvelope(
            {
              failed_url: extractChunkUrl(error.message, ''),
              current_url: window.location.href,
            },
            { source: 'client' }
          )
        );
      } catch {
        // PostHog SDK must never block reload recovery.
      }
      reloadPage();
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Chunk-load failures are never logged as boundary crashes: a known
    // deploy-timing race isn't an app defect. getDerivedStateFromError
    // already reports a successful recovery via CHUNK_LOAD_RELOAD.
    if (isChunkLoadFailure(error.message, '')) {
      // This.state reflects this catch's committed outcome. hasError:false
      // means the reload is genuinely in flight with nothing left to log.
      // hasError:true means the user is looking at the fallback right now —
      // either recovery was unavailable this time (offline, or the one-shot
      // slot already spent this session) or, per the class-level doc above,
      // recovery succeeded but the retry re-threw and burned the slot before
      // committing. Neither is an app defect, but a user parked on a
      // dead-end fallback is worth a signal — logger.warn (unlike
      // logger.error) never reaches PostHog error tracking, so this adds
      // visibility without adding noise to the issue stream.
      if (this.state.hasError) {
        logger.warn('Error boundary: chunk-load recovery unavailable', {
          error,
          componentStack: errorInfo.componentStack,
        });
      }
      return;
    }

    // Route through logger.error: its client sink emits a structured
    // captureException. The previous manual posthog.capture('$exception', ...)
    // lacked $exception_list, which the ingestion pipeline rejects — so
    // boundary crashes never actually reached error tracking. `source`
    // overrides the sink's default 'client_logger' so boundary crashes stay
    // distinguishable in PostHog.
    logger.error('Error boundary caught', {
      error,
      componentStack: errorInfo.componentStack,
      source: 'error_boundary',
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex min-h-screen items-center justify-center p-8">
            <div className="max-w-2xl w-full">
              <EmptyState
                icon={AlertTriangle}
                title="Something went wrong"
                description="We've been notified and are looking into it. Please try refreshing the page."
                destructive
                action={{
                  label: 'Reload page',
                  onClick: () => reloadPage(),
                }}
              />
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
